import { KIND as TagKind } from 'baseui/tag';
import keyBy from 'lodash/keyBy';
import { FRAME_CLASSIFICATION_ID } from '../../../../common/hooks/use-frame-classification-annotation';
import { TASK_CLASSIFICATION_ID } from '../../../../common/hooks/use-task-classification-annotation';
import type { ReviewAnnotationsT, ReviewAnnotationT } from '../../../../common/review-mode-side-panel';
import { REVIEW_ANNOTATION_TYPE } from '../../../../common/review-mode-side-panel';
import type { DynamicAttributeT, RelationshipAttributeT } from '../../../../common/review-mode-side-panel/types';
import type { FrameRangeT, GenericAnnotationT, RangeSpanT } from '../../../../common/types/types';
import type { RelationshipT } from '../../../../modernization-common/relationships';
import type { ModernizedGenericAnnotation } from '../../../../modernization-common/side-panel';
import type { AttributesT } from '../../../../modernization-common/side-panel/annotations/types';
import { ANNOTATION_TYPE, DYNAMIC_ATTRIBUTE_LS_TAG, DYNAMIC_ATTRIBUTE_TAG } from '../../../labelling/constants';
import { filterDynamicAnswers } from '../../../labelling/reducers/utils/utils';
import type { AnnotationT } from '../../../labelling/types';
import { TimeDisplayMode } from '../../../types';
import { getFrameToTime } from '../../../utils/frame-time';
import type { AnnotationWithFrameRangesT } from '../../atoms/relationship-atoms';
import type { DynamicAnswerSpanT } from '../../studio-store/types';
type TGetTagsForAnnotationProps = {
  annotation: Pick<GenericAnnotationT, 'metadata'>;
  annotationSpan?: RangeSpanT[];
  timeDisplayMode: TimeDisplayMode;
  maxFrames: number;
  frameRate: number | null | undefined;
};
export const getFrameRangeLabel = ({
  start,
  end
}: {
  start: number;
  end: number;
}, {
  collapseSingleFrame = false
}: {
  collapseSingleFrame?: boolean;
} = {}) => collapseSingleFrame && start === end ? `${start + 1}` : `${start + 1} -> ${end + 1}`;
export const getTagsForAnnotation = ({
  annotation,
  annotationSpan,
  timeDisplayMode,
  maxFrames,
  frameRate
}: TGetTagsForAnnotationProps) => {
  const tags: AttributesT[] = [];
  if (annotation.metadata?.answers) {
    // Currently only add tags for questionnaire
    Object.values(annotation.metadata.answers).forEach(({
      answers
    }) => {
      const answer = answers.join(', ').trim();
      if (answer) {
        tags.push({
          label: answer,
          kind: TagKind.neutral
        });
      }
    });
  }
  if (annotationSpan && annotationSpan.length > 0) {
    const showTimeBasedView = timeDisplayMode === TimeDisplayMode.TIMESTAMP && !!frameRate;

    // Create a separate tag entry for each range span
    annotationSpan.forEach(span => {
      if (span.start === span.end) {
        const label = showTimeBasedView ? getFrameToTime({
          frame: span.start,
          maxFrames,
          frameRate
        }) : `${span.start + 1}`;
        tags.push({
          label,
          kind: TagKind.accent
        });
      } else {
        const startLabel = showTimeBasedView ? getFrameToTime({
          frame: span.start,
          maxFrames,
          frameRate
        }) : `${span.start + 1}`;
        const endLabel = showTimeBasedView ? getFrameToTime({
          frame: span.end,
          maxFrames,
          frameRate
        }) : `${span.end + 1}`;
        tags.push({
          label: `${startLabel} -> ${endLabel}`,
          kind: TagKind.accent
        });
      }
    });
  }
  return tags;
};
type TMapAnnotationToSidePanelAnnotationProps = {
  id: string;
  annotation: AnnotationT;
  annotationSpan?: RangeSpanT[];
  timeDisplayMode: TimeDisplayMode;
  maxFrames: number;
  frameRate: number | null | undefined;
};
export const mapAnnotationToSidePanelAnnotation = ({
  id,
  annotation,
  annotationSpan,
  timeDisplayMode,
  maxFrames,
  frameRate
}: TMapAnnotationToSidePanelAnnotationProps) => {
  return {
    ...annotation,
    id,
    tags: getTagsForAnnotation({
      annotation,
      annotationSpan,
      timeDisplayMode,
      frameRate,
      maxFrames
    }),
    canCopyObjectInstance: annotation.annotationType !== ANNOTATION_TYPE.SEGMENTATION
  };
};
type DynamicAttributesOptionsT = {
  includeAttributeIndex?: boolean;
};

/**
 * Returns the dynamic attributes for an annotation in the form of an object containing the key as
 * attribute and the value which is the range of frames that the attribute is present in.
 * @param annotationId - The id of the annotation
 * @param rangeSpan - The range of frames that the annotation is present in
 * @param annotationFrames - The frames that the annotation is present in
 * @returns The dynamic attributes for the annotation
 */
export const getDynamicAttributes = (annotationId: string, rangeSpan: RangeSpanT[] | undefined, dynamicAnswers: DynamicAnswerSpanT[], options: DynamicAttributesOptionsT = {}): DynamicAttributeT[] => {
  if (!rangeSpan || rangeSpan.length === 0) {
    return [];
  }
  const dynamicAnswerRanges: {
    [questionId: string]: {
      question: string;
      answerToRanges: {
        [answer: string]: Array<[number, number]>;
      };
    };
  } = {};
  const questionIndexMap: {
    [questionId: string]: number;
  } = {};
  let attributeCounter = 0;
  dynamicAnswers.forEach(({
    frameSpan,
    answers
  }) => {
    Object.entries(answers).reduce((acc, [questionId, answerData]) => {
      const {
        question,
        answers: answerValues,
        tags
      } = answerData;
      if (!tags || !(tags.includes(DYNAMIC_ATTRIBUTE_TAG) || tags.includes(DYNAMIC_ATTRIBUTE_LS_TAG))) {
        return acc;
      }
      if (!acc[questionId]) {
        attributeCounter++;
        questionIndexMap[questionId] = attributeCounter;
        acc[questionId] = {
          question,
          answerToRanges: {}
        };
      }
      answerValues.forEach(answer => {
        if (typeof answer !== 'string') {
          return;
        }
        const questionData = acc[questionId];
        if (!questionData) {
          return;
        }
        if (!questionData.answerToRanges[answer]) {
          questionData.answerToRanges[answer] = [];
        }
        const ranges = questionData.answerToRanges[answer];
        if (!ranges) {
          return;
        }
        const lastRange = ranges[ranges.length - 1];
        if (lastRange && lastRange[1] === frameSpan.start - 1) {
          lastRange[1] = frameSpan.end;
        } else {
          ranges.push([frameSpan.start, frameSpan.end]);
        }
      });
      return acc;
    }, dynamicAnswerRanges);
  });
  const result: DynamicAttributeT[] = [];
  const includeAttributeIndex = options.includeAttributeIndex ?? true;
  Object.entries(dynamicAnswerRanges).forEach(([questionId, {
    answerToRanges
  }]) => {
    const attributeIndex = questionIndexMap[questionId];
    const answersList = Object.keys(answerToRanges);
    answersList.forEach(answer => {
      const ranges = answerToRanges[answer];
      if (!ranges) {
        return;
      }
      const rangeStrings = ranges.map(([start, end]) => getFrameRangeLabel({
        start,
        end
      }));
      const valueStr = rangeStrings.join(', ');
      result.push({
        key: includeAttributeIndex ? `${answer} (Attribute ${attributeIndex})` : answer,
        value: valueStr
      });
    });
  });
  return result;
};
export const getRelationshipAttributes = (annotationId: string, relationships: Record<string, RelationshipT>, allAnnotations: Record<string, AnnotationWithFrameRangesT>): RelationshipAttributeT[] => {
  return Object.values(relationships).filter(r => r.sourceAnnotationId === annotationId).map(r => {
    const targetAnnotation = allAnnotations[r.targetAnnotationId];
    const targetTitle = targetAnnotation?.annotationLabel.title.trim() ?? '';
    const targetAnnotationColor = targetAnnotation?.annotationLabel.style.strokeColor;
    return {
      relationshipLabelName: r.relationshipLabel.relationshipLabelName,
      targetAnnotationName: `${targetTitle} [${r.targetAnnotationId}]`,
      targetAnnotationColor,
      frameRanges: r.frameRanges
    };
  });
};
export const getReviewAnnotations = (annotations: ModernizedGenericAnnotation[], annotationIdToSpanMap: {
  [key: string]: RangeSpanT[];
}, getAnnotationDynamicAnswers: (id: string) => DynamicAnswerSpanT[], relationships: Record<string, RelationshipT>, allAnnotations: Record<string, AnnotationWithFrameRangesT>, taskClassificationAnnotation?: ReviewAnnotationT | null, frameClassificationAnnotation?: ReviewAnnotationT | null): ReviewAnnotationsT => {
  const nonFrameClassificationAnnotations = annotations.filter(a => a.annotationType !== ANNOTATION_TYPE.FRAME_CLASSIFICATION);
  const reviewAnnotations: ReviewAnnotationT[] = nonFrameClassificationAnnotations.map(annotationInfo => {
    const answers = annotationInfo.metadata?.answers || {};
    const staticAnswers = filterDynamicAnswers(answers);
    const classificationAttributes = Object.values(staticAnswers).map(({
      question,
      answers
    }) => ({
      question,
      answers
    }));
    const rangeSpan = annotationIdToSpanMap[annotationInfo.id];
    const dynamicAttributes = getDynamicAttributes(annotationInfo.id, rangeSpan, getAnnotationDynamicAnswers(annotationInfo.id));
    const allRelationshipAttributes = getRelationshipAttributes(annotationInfo.id, relationships, allAnnotations);
    const instances = annotationIdToSpanMap[annotationInfo.id]?.map(instance => ({
      key: 'Frame',
      startIndex: instance.start + 1,
      endIndex: instance.end + 1
    }));
    const attributes: ReviewAnnotationT['attributes'] = rangeSpan?.map(span => {
      const relationshipAttributes = allRelationshipAttributes.filter(rel => rel.frameRanges.length === 0 || rel.frameRanges.some(({
        startFrameIndex,
        endFrameIndex
      }) => startFrameIndex <= span.end && endFrameIndex >= span.start));
      return {
        classificationAttributes,
        // will update this once we have support for dynamic classification per frame range
        dynamicAttributes,
        relationshipAttributes
      };
    });
    const annotationId = annotationInfo.id;
    const annotationTitle = annotationInfo.annotationLabel.title;
    const annotationName = annotationTitle ? `[${annotationId}] ${annotationTitle}` : annotationId;
    return {
      id: annotationId,
      type: REVIEW_ANNOTATION_TYPE.ANNOTATION,
      label: annotationTitle,
      name: annotationName,
      attributes,
      instances,
      hidden: annotationInfo.hidden ?? false
    };
  });
  const result = keyBy(reviewAnnotations, 'id');
  if (taskClassificationAnnotation) {
    result[TASK_CLASSIFICATION_ID] = taskClassificationAnnotation;
  }
  if (frameClassificationAnnotation) {
    result[FRAME_CLASSIFICATION_ID] = frameClassificationAnnotation;
  }
  return result;
};
export const getDeleteLinkedRelationshipsMessage = (count: number) => {
  if (!count) return null;
  return `${count} associated relationship${count > 1 ? 's' : ''} in the selected frame range will also be deleted.`;
};
export const isOverlappingFrameRange = (frameRange1: FrameRangeT, frameRange2: FrameRangeT) => {
  return frameRange1.startFrameIndex <= frameRange2.endFrameIndex && frameRange1.endFrameIndex >= frameRange2.startFrameIndex;
};